import React, { useState, useEffect, useRef, useCallback } from "react";
import SidebarGastos from "./SidebarGastos";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Swal from "sweetalert2";
import ListaGastos from "../components/ListaGastos";

const FILA_NUEVA = {
  descripcion: "",
  noParte: "",
  cantidad: 0,
  precioUnitario: 0,
  createdAt: serverTimestamp(),
  listaId: "",
  listaNombre: "",
};

/* helpers */
const ssKey = (o, k) => `gastosView:${o}:${k}`;
const ssGet = (k, def) => {
  try {
    return JSON.parse(sessionStorage.getItem(k) ?? JSON.stringify(def));
  } catch {
    return def;
  }
};
const ssSet = (k, v) => {
  try {
    sessionStorage.setItem(k, JSON.stringify(v));
  } catch {}
};

export default function GastosView({
  ordenId,
  hideCTA = false, // se deja por compatibilidad, aunque no se use
  onToggleList = () => {},
}) {
  const { formatCurrency, trimLeadingZeros, calcDashboard } =
    useFunctionsPrevia();

  const [dash, setDash] = useState({
    totalCarpetas: 0,
    ingresos: 0,
    gastos: 0,
    balance: 0,
  });

  const [listas, setListas] = useState([]);
  const [listaSel, setListaSel] = useState(null);
  const [productos, setProductos] = useState([]);
  const listeners = useRef({});
  const firstLoadOK = useRef({});
  const masterProd = useRef([]);
  const activeLista = useRef(ssGet(ssKey(ordenId, "activeLista"), null));
  const [isEditable, setIsEditable] = useState(false);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [carpetasReady, setCarpetasReady] = useState(false);
  const [newRow, setNewRow] = useState({
    descripcion: "",
    noParte: "",
    cantidad: "",
    precioUnitario: "",
  });
  const noFoldersAlertShown = useRef(false);

  const showNoFoldersAlert = useCallback(() => {
    Swal.fire({
      title: "Aún no tienes carpetas de gastos",
      html: "Dale clic al botón azul <b>+</b> del panel izquierdo para crear tu primera carpeta y empezar a agregar gastos.",
      icon: "info",
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0d6efd",
      width: 480,
    });
  }, []);

  // Siempre estamos en modo lista mientras este componente esté montado
  useEffect(() => {
    onToggleList(true);
    return () => onToggleList(false);
  }, [onToggleList]);

  // Alert de "no tienes carpetas"
  useEffect(() => {
    if (carpetasReady && listas.length === 0 && !noFoldersAlertShown.current) {
      noFoldersAlertShown.current = true;
      showNoFoldersAlert();
    }

    if (listas.length > 0) {
      noFoldersAlertShown.current = false;
    }
  }, [carpetasReady, listas.length, showNoFoldersAlert]);

  const refreshDashboard = useCallback(
    () => setDash(calcDashboard(masterProd.current, listas)),
    [calcDashboard, listas]
  );

  useEffect(() => {
    if (listas.length) refreshDashboard();
  }, [listas, refreshDashboard]);

  const clearNewRow = () =>
    setNewRow({ descripcion: "", noParte: "", cantidad: 0, precioUnitario: 0 });

  /* listeners de carpetas */
  useEffect(() => {
    if (!ordenId) return;

    setCarpetasReady(false);

    const carpetasRef = collection(db, `pedidos/${ordenId}/listasGastos`);
    return onSnapshot(carpetasRef, (snap) => {
      const carpetas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setListas(carpetas);
      setCarpetasReady(true);

      // limpiar listeners de carpetas que ya no existen
      Object.keys(listeners.current).forEach((id) => {
        if (!carpetas.find((c) => c.id === id)) {
          listeners.current[id]();
          delete listeners.current[id];
          delete firstLoadOK.current[id];
          masterProd.current = masterProd.current.filter(
            (p) => p.listaId !== id
          );
          if (activeLista.current === id) {
            activeLista.current = null;
            ssSet(ssKey(ordenId, "activeLista"), null);
          }
        }
      });

      // listeners nuevos / vigentes
      carpetas.forEach(({ id }) => {
        if (listeners.current[id]) return;
        const ref = collection(
          db,
          `pedidos/${ordenId}/listasGastos/${id}/productosGastos`
        );
        listeners.current[id] = onSnapshot(ref, (snapProd) => {
          const alreadyInit = firstLoadOK.current[id];
          const hasRealChange = snapProd
            .docChanges()
            .some((ch) => ch.type !== "added" || alreadyInit);

          if (hasRealChange) {
            activeLista.current = id;
            ssSet(ssKey(ordenId, "activeLista"), id);
          }
          firstLoadOK.current[id] = true;

          masterProd.current = [
            ...masterProd.current.filter((p) => p.listaId !== id),
            ...snapProd.docs.map((d) => ({
              id: d.id,
              listaId: id,
              ...d.data(),
            })),
          ];
          refreshDashboard();
        });
      });
    });
  }, [ordenId, refreshDashboard]);

  /* listener productos para la tabla de la lista seleccionada */
  useEffect(() => {
    if (!ordenId || !listaSel?.id) {
      setProductos([]);
      return;
    }

    const ref = collection(
      db,
      `pedidos/${ordenId}/listasGastos/${listaSel.id}/productosGastos`
    );

    // 👇 quitamos includeMetadataChanges para que solo dispare cuando cambian los docs
    const unsub = onSnapshot(ref, (snap) => {
      const getMs = (t) =>
        t instanceof Timestamp ? t.toMillis() : Number.MAX_SAFE_INTEGER;

      const prods = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));

      // 👇 si el arreglo nuevo es IGUAL al anterior, no actualizamos estado
      setProductos((prev) => {
        if (prev.length === prods.length) {
          const same = prev.every((p, idx) => {
            const q = prods[idx];
            if (!q) return false;
            return (
              p.id === q.id &&
              p.descripcion === q.descripcion &&
              p.noParte === q.noParte &&
              p.cantidad === q.cantidad &&
              p.precioUnitario === q.precioUnitario
            );
          });

          if (same) {
            return prev; // no hay cambio real → NO re-render → NO parpadeo
          }
        }
        return prods;
      });
    });

    return unsub;
  }, [ordenId, listaSel?.id]);

  // activar edición automática si la carpeta seleccionada no tiene productos
  useEffect(() => {
    if (listaSel?.id && productos.length === 0) {
      setIsEditable(true);
    }
  }, [listaSel?.id, productos.length]);

  /* selección inicial de carpeta */
  useEffect(() => {
    if (!listas.length) return;
    const prefer = activeLista.current || listaSel?.id || listas[0].id;
    if (listaSel?.id !== prefer) {
      setListaSel(listas.find((l) => l.id === prefer) ?? listas[0]);
    }
  }, [listas, listaSel?.id]);

  useEffect(() => {
    if (listaSel?.id) {
      setIsEditable(false);
      setIsAddingRow(false);
      clearNewRow();
    }
  }, [listaSel?.id]);

  const afterWrite = (idCarp) => {
    activeLista.current = idCarp;
    ssSet(ssKey(ordenId, "activeLista"), idCarp);
    refreshDashboard();
  };

  const handleChange = async (i, field, val) => {
    const p = productos[i];
    if (!p) return;
    await updateDoc(
      doc(
        db,
        `pedidos/${ordenId}/listasGastos/${listaSel.id}/productosGastos`,
        p.id
      ),
      {
        [field]:
          field === "cantidad" || field === "precioUnitario"
            ? parseFloat(val) || 0
            : val,
        listaNombre: listaSel.nombre,
      }
    );
    afterWrite(listaSel.id);
  };

  const removeRow = async (i) => {
    const p = productos[i];
    if (!p) return;
    await deleteDoc(
      doc(
        db,
        `pedidos/${ordenId}/listasGastos/${listaSel.id}/productosGastos`,
        p.id
      )
    );
    afterWrite(listaSel.id);
  };

  // Texto que verá el usuario en el título mientras carga
  const tieneCarpetas = listas.length > 0;

  // Texto neutro para el encabezado y pestañas
  const listaNombreVisible = tieneCarpetas
    ? listaSel?.nombre || "Selecciona una carpeta de gastos"
    : "Gastos";

  /* RENDER – LISTA + TABLA SIEMPRE (sin esperar a Firestore) */
  return (
    <div className="container-gastos--row">
      <SidebarGastos
        idOrden={ordenId}
        listas={listas}
        setListas={setListas}
        onSelectLista={(l) => {
          if (!l) return;
          activeLista.current = l.id;
          ssSet(ssKey(ordenId, "activeLista"), l.id);
          setListaSel(l);
        }}
        onListaEliminada={(id) => {
          if (id === activeLista.current) {
            activeLista.current = null;
            ssSet(ssKey(ordenId, "activeLista"), null);
          }
          if (id === listaSel?.id) setListaSel(null);
        }}
        totalCarpetas={dash.totalCarpetas}
        formatCurrency={formatCurrency}
      />

      {/* SI NO HAY CARPETAS: PANEL ESTÁTICO, SIN TABLA, SIN PARPADEO */}
      {!tieneCarpetas ? (
        <div
          className="gastos-main"
          style={{
            flex: 1,
            display: "flex",
            paddingLeft: 28,
            paddingRight: 8,
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: 0,
            marginBottom: 0,
            boxSizing: "border-box",
            alignItems: "stretch",
          }}
        >
          <div
            className="list-view"
            style={{
              border: "1px solid #E6EDFF",
              borderRadius: 6,
              background: "#FFFFFF",
              padding: "16px 24px",
              width: "100%",
              boxSizing: "border-box",
              marginTop: 2,
              marginBottom: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start", // 👈 ANTES: "center"
            }}
          >
            <h3 style={{ marginBottom: 8 }}>Gastos</h3>
            <p style={{ color: "#6B7280", fontSize: 14 }}>
              Empieza creando una carpeta de gastos en el panel izquierdo para
              registrar tus gastos. Esta vista se quedará fija hasta que exista
              al menos una carpeta.
            </p>
          </div>
        </div>
      ) : (
        /* SI YA HAY CARPETAS: AHORA SÍ RENDERIZAMOS ListaGastos */
        <ListaGastos
          listaNombre={listaNombreVisible}
          isEditable={isEditable}
          onToggleEdit={() => setIsEditable((v) => !v)}
          /* tabla */
          productos={productos}
          onChangeCell={handleChange}
          onRemoveRow={removeRow}
          isAddingRow={isAddingRow}
          newRow={newRow}
          onNewRowChange={setNewRow}
          trimLeadingZeros={trimLeadingZeros}
          formatCurrency={formatCurrency}
          /* resumen */
          showResumen={productos.length > 0}
          /* FAB */
          canShowFab={
            !isAddingRow &&
            !!listaSel?.id &&
            (isEditable || productos.length === 0)
          }
          onFabClick={() => {
            if (!listaSel?.id) {
              Swal.fire(
                "Selecciona o crea una carpeta de gastos primero",
                "",
                "info"
              );
              return;
            }
            if (!isEditable) setIsEditable(true);
            setIsAddingRow(true);
          }}
          /* acciones guardar / cancelar de la nueva fila */
          onGuardarNuevaFila={async () => {
            if (!listaSel?.id) {
              Swal.fire(
                "Selecciona o crea una carpeta de gastos primero",
                "",
                "info"
              );
              setIsAddingRow(false);
              return;
            }
            await addDoc(
              collection(
                db,
                `pedidos/${ordenId}/listasGastos/${listaSel.id}/productosGastos`
              ),
              {
                ...FILA_NUEVA,
                descripcion: newRow.descripcion,
                noParte: newRow.noParte,
                cantidad: parseFloat(newRow.cantidad) || 0,
                precioUnitario: parseFloat(newRow.precioUnitario) || 0,
                listaId: listaSel.id,
                listaNombre: listaSel.nombre,
                createdAt: serverTimestamp(),
              }
            );
            afterWrite(listaSel.id);
            clearNewRow();
            setIsAddingRow(false);
          }}
          onCancelarNuevaFila={async () => {
            const res = await Swal.fire({
              title: "¿Cancelar este gasto?",
              text: "Se perderán los datos escritos. ¿Deseas continuar?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Sí, cancelar",
              cancelButtonText: "No, seguir editando",
            });

            if (res.isConfirmed) {
              clearNewRow();
              setIsAddingRow(false);
              setIsEditable(false); // seguimos apagando edición
            }
          }}
        />
      )}
    </div>
  );
}
